import { CancelOutlined, ErrorOutlined, HourglassTopOutlined, TaskAltOutlined } from "@mui/icons-material";
import { Box, Paper, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import MetaChip, { type MetaChipTone } from "../../components/UIToolkit/Badges/MetaChip";
import { InlineHint } from "../../components/UIToolkit/molecules/InlineHint";
import SystemBanner, { type SystemBannerTone } from "../../components/UIToolkit/molecules/SystemBanner";
import { type StageTrackStage, SystemStageTrack } from "../../components/UIToolkit/molecules/SystemStageTrack";
import { type StatusChecklistItem, type StatusChecklistItemState, SystemStatusChecklist } from "../../components/UIToolkit/molecules/SystemStatusChecklist";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import type { OnboardingJob, OnboardingJobItemStatus, OnboardingJobStage, OnboardingJobStatus, OnboardingJobType } from "../../services/backend.service";
import { isOnboardingJobActive } from "./useOnboardingJobs";

const ANALYSIS_STAGES: readonly OnboardingJobStage[] = ["queued", "reading_files", "analyzing_files", "checking_analysis", "ready"];
const TRANSFORM_STAGES: readonly OnboardingJobStage[] = ["queued", "reading_sheets", "transforming", "matching_staff", "checking_results", "ready"];

export const onboardingStagesForJobType = (jobType: OnboardingJobType): readonly OnboardingJobStage[] =>
	jobType === "auto_prompt_analysis" ? ANALYSIS_STAGES : TRANSFORM_STAGES;

export const onboardingStageIndex = (jobType: OnboardingJobType, stage: OnboardingJobStage): number | null => {
	const index = onboardingStagesForJobType(jobType).indexOf(stage);
	return index === -1 ? null : index;
};

export const isOnboardingJobArtifactExpired = (job: Pick<OnboardingJob, "expires_at" | "artifacts_deleted_at">, now: DateTime): boolean => {
	if (job.artifacts_deleted_at !== null) return true;
	const expiresAt = DateTime.fromISO(job.expires_at, { setZone: true });
	return !expiresAt.isValid || expiresAt <= now;
};

const STATUS_TONE: Record<OnboardingJobStatus, MetaChipTone> = {
	queued: "neutral",
	running: "primary",
	succeeded: "success",
	partially_succeeded: "warning",
	failed: "error",
	cancel_requested: "warning",
	cancelled: "neutral",
};

const CHECKLIST_STATE: Record<OnboardingJobItemStatus, StatusChecklistItemState> = {
	queued: "pending",
	running: "active",
	succeeded: "done",
	failed: "blocked",
	cancelled: "blocked",
};

const terminalOffRamp = (status: OnboardingJobStatus, t: (key: string) => string) => {
	if (status === "failed") return { label: t("onboarding_jobs.status.failed"), tone: "error" as const };
	if (status === "cancelled") return { label: t("onboarding_jobs.status.cancelled"), tone: "warning" as const };
	return undefined;
};

const bannerFor = (
	job: OnboardingJob,
	t: (key: string, values?: Record<string, unknown>) => string,
): { tone: SystemBannerTone; title: string; description: string } => {
	if (job.status === "queued") {
		return { tone: "info", title: t("onboarding_jobs.banner.queued_title"), description: t("onboarding_jobs.banner.queued_description") };
	}
	if (job.status === "running") {
		return { tone: "info", title: t("onboarding_jobs.banner.running_title"), description: t("onboarding_jobs.banner.running_description") };
	}
	if (job.status === "cancel_requested") {
		return { tone: "warning", title: t("onboarding_jobs.banner.stopping_title"), description: t("onboarding_jobs.banner.stopping_description") };
	}
	if (job.status === "partially_succeeded") {
		return { tone: "warning", title: t("onboarding_jobs.banner.partial_title"), description: t("onboarding_jobs.banner.partial_description") };
	}
	if (job.status === "failed") {
		return {
			tone: "error",
			title: t("onboarding_jobs.banner.failed_title"),
			description: job.user_error_key ? t(job.user_error_key) : t("onboarding_jobs.banner.failed_description"),
		};
	}
	if (job.status === "cancelled") {
		return { tone: "warning", title: t("onboarding_jobs.banner.cancelled_title"), description: t("onboarding_jobs.banner.cancelled_description") };
	}
	return { tone: "success", title: t("onboarding_jobs.banner.ready_title"), description: t("onboarding_jobs.banner.ready_description") };
};

type Props = {
	job: OnboardingJob;
	timezone: string;
	onCancel?: () => void;
	onRetryItem?: (itemId: string) => void;
	isCancelling?: boolean;
	retryingItemId?: string;
	compact?: boolean;
	onRerun?: () => void;
};

export function OnboardingJobProgress(props: Props) {
	const { job, timezone, onCancel, onRetryItem, retryingItemId, onRerun } = props;
	const isCancelling = props.isCancelling === true;
	const compact = props.compact === true;
	const { t } = useTranslation();
	const stages = onboardingStagesForJobType(job.job_type);
	const reachedIndex = onboardingStageIndex(job.job_type, job.stage);
	const trackStages: StageTrackStage[] = stages.map((stage, index) => ({
		id: stage,
		label: t(`onboarding_jobs.stage.${stage}`),
		state: reachedIndex !== null && index < reachedIndex ? "done" : index === reachedIndex ? "current" : "upcoming",
		dataTestId: `onboarding-run-${job.id}-stage-${stage}`,
	}));
	const banner = bannerFor(job, t);
	const createdAt = DateTime.fromISO(job.created_at, { setZone: true }).setZone(timezone);
	const artifactsExpired = isOnboardingJobArtifactExpired(job, DateTime.now());
	const checklistItems: StatusChecklistItem[] = job.items.map((item) => ({
		id: item.id,
		label: item.display_filename,
		description:
			item.status === "failed"
				? item.user_error_key
					? t(item.user_error_key)
					: t("onboarding_jobs.item.failed_description")
				: item.failed_tabs.length > 0
					? t("onboarding_jobs.item.sheet_warnings", { count: item.failed_tabs.length })
					: undefined,
		state: CHECKLIST_STATE[item.status],
		statusLabel: t(`onboarding_jobs.item_status.${item.status}`),
		dataTestId: `onboarding-run-${job.id}-item-${item.id}`,
	}));

	return (
		<Paper
			variant="outlined"
			data-testid={`onboarding-run-${job.id}`}
			sx={{ p: compact ? 2 : 2.5, display: "flex", flexDirection: "column", gap: compact ? 1.5 : 2, minWidth: 0 }}
		>
			<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
				<Box sx={{ minWidth: 0 }}>
					<Typography variant={compact ? "subtitle1" : "h6"} sx={{ fontWeight: 700 }}>
						{t(`onboarding_jobs.type.${job.job_type}`)}
					</Typography>
					<Typography variant="caption" sx={{ color: "text.secondary" }}>
						{t("onboarding_jobs.started_at", { time: createdAt.toLocaleString(DateTime.DATETIME_MED) })}
					</Typography>
				</Box>
				<MetaChip
					label={t(`onboarding_jobs.status.${job.status}`)}
					tone={STATUS_TONE[job.status]}
					size="medium"
					icon={
						job.status === "failed" ? (
							<ErrorOutlined />
						) : job.status === "cancelled" ? (
							<CancelOutlined />
						) : isOnboardingJobActive(job) ? (
							<HourglassTopOutlined />
						) : (
							<TaskAltOutlined />
						)
					}
					dataTestId={`onboarding-run-${job.id}-status`}
				/>
			</Box>

			{reachedIndex === null ? (
				<SystemBanner
					tone="error"
					title={t("onboarding_jobs.unknown_stage_title")}
					description={t("onboarding_jobs.unknown_stage_description")}
					dataTestId={`onboarding-run-${job.id}-unknown-stage`}
				/>
			) : (
				<SystemStageTrack
					stages={trackStages}
					offRamp={terminalOffRamp(job.status, t)}
					tone={job.status === "partially_succeeded" ? "warning" : "primary"}
					ariaLabel={t("onboarding_jobs.stage_track_label")}
					dataTestId={`onboarding-run-${job.id}-stage-track`}
				/>
			)}

			<SystemBanner tone={banner.tone} title={banner.title} description={banner.description} dataTestId={`onboarding-run-${job.id}-banner`} />

			{artifactsExpired && (
				<SystemBanner
					tone="warning"
					title={t("onboarding_jobs.expired_title")}
					description={t("onboarding_jobs.expired_description")}
					action={
						onRerun ? (
							<SystemButton buttonVariant="secondary" onClick={onRerun} data-testid={`onboarding-run-${job.id}-rerun`}>
								{t("onboarding_jobs.expired_action")}
							</SystemButton>
						) : undefined
					}
					dataTestId={`onboarding-run-${job.id}-expired`}
				/>
			)}

			{isOnboardingJobActive(job) && (
				<InlineHint tone="neutral" dataTestId={`onboarding-run-${job.id}-leave-hint`}>
					{t("onboarding_jobs.leave_hint")}
				</InlineHint>
			)}

			{job.job_type === "csv_transform_batch" && checklistItems.length > 0 && (
				<SystemStatusChecklist
					items={checklistItems}
					title={t("onboarding_jobs.files_title", { completed: job.completed_items, total: job.total_items })}
					dataTestId={`onboarding-run-${job.id}-files`}
					stagger={false}
				/>
			)}

			<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
				{!artifactsExpired &&
					job.items
						.filter((item) => item.status === "failed")
						.map((item) => (
							<SystemButton
								key={item.id}
								buttonVariant="secondary"
								size="small"
								onClick={() => onRetryItem?.(item.id)}
								disabled={!onRetryItem || retryingItemId === item.id}
								loading={retryingItemId === item.id}
								data-testid={`onboarding-run-${job.id}-retry-${item.id}`}
							>
								{t("onboarding_jobs.retry_file", { filename: item.display_filename })}
							</SystemButton>
						))}
				{isOnboardingJobActive(job) && job.status !== "cancel_requested" && (
					<SystemButton
						buttonVariant="secondary"
						size="small"
						onClick={onCancel}
						disabled={!onCancel || isCancelling}
						loading={isCancelling}
						data-testid={`onboarding-run-${job.id}-cancel`}
					>
						{t("onboarding_jobs.cancel")}
					</SystemButton>
				)}
			</Box>
		</Paper>
	);
}
